import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowRight, Target, CheckCircle2 } from "lucide-react";
import { POSTURE_PATTERNS } from "@/lib/posturePatterns";
import { useAssessment } from "@/context/AssessmentContext";

export default function PosturePattern() {
  const { data, update } = useAssessment();
  const [selected, setSelected] = useState(data.pattern?.id || null);
  const [testMeasure, setTestMeasure] = useState(data.pattern?.measurement || "");
  const [positive, setPositive] = useState(data.pattern?.positive ?? null);
  const navigate = useNavigate();
  const selfTestRef = useRef(null);

  // Pre-select from AI hint if present
  useEffect(() => {
    if (!selected && data.vision?.likely_patterns?.length) {
      setSelected(data.vision.likely_patterns[0].id);
    }
  }, [data.vision, selected]);

  // When a pattern is picked (or changed), smooth-scroll down to the
  // "Quick Check at Home" panel so the user immediately sees the new section.
  useEffect(() => {
    if (!selected) return;
    // Wait a tick for the panel to mount, then scroll
    const t = setTimeout(() => {
      selfTestRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [selected]);

  const pattern = POSTURE_PATTERNS.find((p) => p.id === selected);
  const suggested = data.vision?.likely_patterns?.map((p) => p.id) || [];

  const proceed = () => {
    if (!pattern) return;
    update("pattern", {
      id: pattern.id,
      name: pattern.name,
      plain_name: pattern.plain_name,
      measurement: testMeasure,
      positive,
      tight_muscles: pattern.tight_muscles,
      weak_muscles: pattern.weak_muscles,
    });
    navigate("/muscular-assessment");
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 mb-4 text-sm text-forest font-medium">
        <Target className="w-4 h-4" /> Step 3 · Confirm What's Going On
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest mb-3">
        Which one looks the most like you?
      </h1>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        {suggested.length > 0
          ? "We highlighted the picture that best matches your assessment. You can change it if a different one feels more accurate."
          : "Look at each picture and pick the one that best matches how you stand."}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {POSTURE_PATTERNS.map((p) => {
          const isSuggested = suggested.includes(p.id);
          const isSelected = selected === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              data-testid={`pattern-${p.id}`}
              className={`text-left rounded-xl overflow-hidden bg-white border transition-all group ${
                isSelected
                  ? "border-forest ring-2 ring-forest/30"
                  : isSuggested
                  ? "border-honey"
                  : "border-forest/10 hover:border-forest/40"
              }`}
            >
              <div className="relative aspect-square bg-bone overflow-hidden">
                <img src={p.image} alt={p.plain_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {isSuggested && !isSelected && (
                  <Badge className="absolute top-2 left-2 bg-honey text-forest border-0">Best match</Badge>
                )}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-forest text-bone flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="font-heading font-semibold text-forest">{p.plain_name}</p>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5 mb-2">{p.plain_region}</p>
                <p className="text-xs text-ink/75 leading-relaxed">{p.plain_hint}</p>
              </div>
            </button>
          );
        })}
      </div>

      {pattern && (
        <Card ref={selfTestRef} className="overflow-hidden bg-white border-forest/10 mb-6 scroll-mt-24" data-testid="self-test-panel">
          {pattern.self_test.test_image && (
            <div className="w-full bg-[#E8F1F5] p-3 sm:p-4 border-b border-forest/10">
              <img
                src={pattern.self_test.test_image}
                alt={`${pattern.self_test.plain_name} instructions`}
                className="w-full max-h-[520px] object-contain mx-auto"
              />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {!pattern.self_test.test_image && (
              <div className="relative bg-bone">
                <img src={pattern.image} alt={pattern.plain_name} className="w-full h-full object-cover min-h-[280px]" />
              </div>
            )}
            <div className={`p-6 ${pattern.self_test.test_image ? "md:col-span-2" : ""}`}>
              <p className="text-xs uppercase tracking-[0.2em] text-sage mb-2">Quick check at home</p>
              <h3 className="font-heading text-2xl font-semibold text-forest mb-3">{pattern.self_test.plain_name}</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-ink mb-4">
                {pattern.self_test.steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
              <div className="p-3 rounded-md bg-terracotta/10 border border-terracotta/30 text-sm mb-4">
                <span className="font-medium text-terracotta">What counts as "yes":</span>{" "}
                <span className="text-ink">{pattern.self_test.plain_threshold}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Your measurement (optional)</label>
                  <Input
                    value={testMeasure}
                    onChange={(e) => setTestMeasure(e.target.value)}
                    placeholder='e.g., "3 cm gap"'
                    data-testid="test-measure-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Result</label>
                  <div className="flex gap-2">
                    {[
                      { label: "No", val: false, sub: "seems fine" },
                      { label: "Yes", val: true, sub: "matches me" },
                    ].map((o) => (
                      <button
                        key={o.label}
                        onClick={() => setPositive(o.val)}
                        data-testid={`test-${o.label.toLowerCase() === "yes" ? "positive" : "negative"}`}
                        className={`px-3 py-2 rounded-md border text-sm flex-1 transition-colors ${
                          positive === o.val
                            ? o.val
                              ? "bg-terracotta text-white border-terracotta"
                              : "bg-sage text-bone border-sage"
                            : "border-forest/20 hover:border-forest/40"
                        }`}
                      >
                        <div className="font-medium">{o.label}</div>
                        <div className="text-[11px] opacity-80">{o.sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-forest/10">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-2">Tight muscles</p>
                  <ul className="text-sm space-y-1">
                    {pattern.tight_muscles.map((m) => (
                      <li key={m.name}>• <span className="font-medium">{m.plain}</span> <span className="text-muted-foreground text-xs">({m.name})</span></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-sage mb-2">Weak muscles</p>
                  <ul className="text-sm space-y-1">
                    {pattern.weak_muscles.map((m) => (
                      <li key={m.name}>• <span className="font-medium">{m.plain}</span> <span className="text-muted-foreground text-xs">({m.name})</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button
          onClick={proceed}
          disabled={!pattern}
          className="bg-forest hover:bg-forest/90 text-bone rounded-full px-7"
          data-testid="pattern-continue-btn"
        >
          Next: check the muscles <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
